// @file Managing and executing sound effects
import device from '@@/bits/device'
import { captureMessage } from '@@/bits/error_tracker'
import { useReactiveSet } from '@@/bits/reactivity'
import { requestIdleCallback } from '@@/bits/request_idle_callback'
import { chunk } from 'es-toolkit'
// We need `compat` for async function support for now
import { throttle } from 'es-toolkit/compat'
import type { Howl } from 'howler'

const FREE_UP_MEMORY_TIMEOUT = 15_000 // ms
const CONCURRENT_PRELOAD_LIMIT = device.app || device.mobile ? 6 : 20
const USE_HTML5_AUDIO = (device.safari && (device.mobile || device.tablet)) || device.androidApp

class SfxManager {
  private readonly effects: Map<string, Howl> = new Map()
  private readonly runningEffects = useReactiveSet<string>()
  private freeUpMemoryTimeoutId: number | null = null

  constructor() {
    if (USE_HTML5_AUDIO) {
      // Use a larger pool size than the default (10)
      requestIdleCallback(() => {
        void import('howler').then(({ Howler }) => {
          Howler.html5PoolSize = 100
        })
      })
    }
  }

  /**
   * We have many sound effects. If the user opens the sound effects
   * modal, they may load >100 Howl instances into memory. We don't
   * want to keep that many instances in memory forever, so we clear
   * them after a certain amount of time if they're not used.
   */
  private scheduleFreeUpMemoryIfNotUsed(): void {
    if (this.freeUpMemoryTimeoutId !== null) {
      window.clearTimeout(this.freeUpMemoryTimeoutId)
    }

    this.freeUpMemoryTimeoutId = window.setTimeout(() => {
      requestIdleCallback(() => {
        void this.unloadEffects()
      })
    }, FREE_UP_MEMORY_TIMEOUT)
  }

  public async preloadEffects(urls: string[]): Promise<void> {
    const urlsToLoad = urls.filter((url) => !this.effects.has(url))

    // Chunk the URLs to avoid loading too many at once
    for (const chunkedUrls of chunk(urlsToLoad, CONCURRENT_PRELOAD_LIMIT)) {
      await Promise.allSettled(
        chunkedUrls.map(async (url) => {
          const { Howl } = await import('howler')
          return await new Promise<void>((resolve) => {
            const sound = new Howl({
              src: url,
              format: ['mp3', 'wav'],
              html5: USE_HTML5_AUDIO,
              autoplay: false,
            })

            sound.on('loaderror', (_, error) => {
              const errorCode = error as MediaError['code']
              captureMessage('[SFX] Error loading audio', {
                context: {
                  url,
                  errorCode,
                },
                fingerprint: [String(errorCode)],
              })
              resolve()
            })

            sound.on('load', () => {
              this.effects.set(url, sound)
              resolve()
            })

            sound.load()
          })
        }),
      )
    }

    this.scheduleFreeUpMemoryIfNotUsed()
  }

  public async unloadEffects(): Promise<void> {
    const { Howler } = await import('howler')
    Howler.unload()
    this.effects.clear()
    this.freeUpMemoryTimeoutId = null
  }

  public executeEffect = throttle(
    async (url: string): Promise<void> => {
      if (!this.effects.has(url)) {
        await this.preloadEffects([url])
      }
      const sound = this.effects.get(url)
      if (sound == null) {
        captureMessage('[SFX] Could not load sound effect', {
          context: {
            url,
          },
        })
        return
      }
      return await new Promise<void>((resolve) => {
        sound.on('playerror', (_, error) => {
          captureMessage('[SFX] Error playing audio', {
            context: {
              url,
              error,
            },
            fingerprint: [String(error)],
          })
          resolve()
        })
        sound.on('end', () => {
          this.runningEffects.value.delete(url)
          resolve()
        })
        sound.play()
        this.runningEffects.value.add(url)

        this.scheduleFreeUpMemoryIfNotUsed()
      })
    },
    200,
    {
      leading: true,
      trailing: false,
    },
  )

  public isEffectRunning(url: string): boolean {
    return this.runningEffects.value.has(url)
  }

  public stopEffect(url: string): void {
    const sound = this.effects.get(url)
    if (sound == null) {
      return
    }
    sound.stop()
    this.runningEffects.value.delete(url)
  }

  public stopAllEffects(): void {
    for (const sound of this.effects.values()) {
      sound.stop()
    }
    this.runningEffects.value.clear()
  }
}

export const sfxManager = new SfxManager()
